// src/pages/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate, Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db, storage } from '../firebase';
import {
  collection, query, getDocs, doc, getDoc, setDoc, where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signOut } from 'firebase/auth';

// Styles
import '../styles/AdminDashboard.css';
import '../styles/UserDashboard.css';

// Live preview component (phone mock)
import LivePreview from '../components/LivePreview';

/* ===============================
   AdminLayout
   - يتحقق من صلاحية الأدمن
   - يوفّر Sidebar + Backdrop + Header
   - يحتوي Outlet لعرض الصفحات الفرعية
   =============================== */
export default function AdminLayout() {
  const [user, loadingAuth] = useAuthState(auth);
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // تحكّم فتح/إغلاق السايدبار (للموبايل)
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (loadingAuth) return;
    if (!user) {
      navigate('/login');
      return;
    }
    const checkAdminStatus = async () => {
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists() && userDocSnap.data().isAdmin) {
        setIsAdmin(true);
      } else {
        navigate('/dashboard');
      }
      setCheckingAdmin(false);
    };
    checkAdminStatus();
  }, [user, loadingAuth, navigate]);

  // منع تمرير الصفحة بالخلفية عند فتح المنيو على الموبايل
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  if (loadingAuth || checkingAdmin) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'
      }}>
        Verifying Access...
      </div>
    );
  }
  if (!isAdmin) return null;

  return (
    <div className={`admin-layout ${mobileOpen ? 'sidebar-open' : ''}`}>
      {/* Backdrop (click to close) */}
      <div className="admin-backdrop" onClick={() => setMobileOpen(false)} />

      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-inner">
          <div className="admin-sidebar-header">
            <h1 className="admin-sidebar-title">Admin</h1>
            <button className="admin-sidebar-close" onClick={() => setMobileOpen(false)}>×</button>
          </div>

          <nav className="admin-nav">
            <NavLink to="/admin/users" onClick={() => setMobileOpen(false)}>All Users</NavLink>
            <NavLink to="/dashboard" onClick={() => setMobileOpen(false)}>Back to My Profile</NavLink>
          </nav>

          <button onClick={handleLogout} className="admin-logout-button">
            Logout
          </button>
        </div>
      </aside>

      {/* Header (الهيدر العلوي البسيط) */}
      <header className="admin-header">
        <button
          className={`hamburger ${mobileOpen ? 'is-active' : ''}`}
          aria-label="Toggle sidebar"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(v => !v)}
        >
          <span />
          <span />
          <span />
        </button>

        <div className="admin-header-title">Admin Dashboard</div>

        <div className="admin-header-actions">
          {/* يمكنك إضافة زر إضافي أو صورة بروفايل صغيرة هنا */}
          <Link className="admin-header-link" to="/dashboard">My Profile</Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-content">
        {/* هنا تُعرض الصفحات الفرعية عبر الراوتر */}
        <Outlet />
      </main>
    </div>
  );
}

/* ===============================
   AdminUserList
   - جلب جميع المستخدمين من Firestore
   - جدول احترافي مع روابط التعديل والمعاينة
   =============================== */
export function AdminUserList() {
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const q = query(collection(db, 'users'));
        const querySnapshot = await getDocs(q);
        const allUsers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsersList(allUsers);
      } catch (err) {
        setError('Failed to fetch users. Check security rules.');
        console.error(err);
      }
      setLoading(false);
    };
    fetchAllUsers();
  }, []);

  if (loading) return <div>Loading users...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div className="admin-users-page">
      <div className="admin-page-head">
        <h2>All Users ({usersList.length})</h2>
        <p className="admin-page-sub">Manage public profiles, roles, and themes.</p>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Display Name</th>
              <th>Username</th>
              <th>Email</th>
              <th>Admin?</th>
              <th>Edit</th>
              <th>View Public</th>
            </tr>
          </thead>
          <tbody>
            {usersList.map(u => (
              <tr key={u.id}>
                <td style={{ fontWeight: 700 }}>{u.displayName || '—'}</td>
                <td>{u.username || '—'}</td>
                <td>{u.email || '—'}</td>
                <td>{u.isAdmin ? 'Yes' : 'No'}</td>
                <td>
                  <Link to={`/admin/users/edit/${u.id}`} className="admin-table-link">
                    Edit User
                  </Link>
                </td>
                <td>
                  <a
                    href={`/${u.username || ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="admin-table-link"
                  >
                    View Profile
                  </a>
                </td>
              </tr>
            ))}
            {usersList.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===============================
   AdminEditUser
   - صفحة تعديل بيانات مستخدم محدّد
   - رفع صورة بروفايل إلى Storage
   - تعديل isAdmin + الحقول الأساسية + روابط التواصل
   - معاينة مباشرة عبر LivePreview
   =============================== */
const initialProfileState = {
  uid: '',
  email: '',
  displayName: '',
  username: '',
  profileType: 'personal',
  theme: 'light',
  photoURL: '',
  isAdmin: false,
  links: {
    phone: '',
    email: '',
    whatsapp: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    linkedin: '',
    snapchat: '',
    x: '',
  },
};

export function AdminEditUser() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState(initialProfileState);
  const [imageFile, setImageFile] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // 1) جلب بيانات المستخدم
  useEffect(() => {
    if (!userId) return;
    const fetchProfileData = async () => {
      setLoadingData(true);
      try {
        const userDocRef = doc(db, 'users', userId);
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProfileData(prev => ({
            ...initialProfileState,
            ...data,
            links: { ...initialProfileState.links, ...(data.links || {}) },
          }));
        } else {
          setError('User not found.');
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load user.');
      }
      setLoadingData(false);
    };
    fetchProfileData();
  }, [userId]);

  // 2) مبدّلات الحقول
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'isAdmin') {
      setProfileData(prev => ({ ...prev, isAdmin: checked }));
      return;
    }
    if (name.startsWith('links.')) {
      const k = name.split('.')[1];
      setProfileData(prev => ({ ...prev, links: { ...prev.links, [k]: value } }));
      return;
    }
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) setImageFile(e.target.files[0]);
  };

  // 3) حفظ التعديلات
  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    setError(null);
    try {
      // تأكد من تفرّد الـ username (باستثناء هذا المستخدم)
      if (profileData.username) {
        const usernameQ = query(collection(db, 'users'), where('username', '==', profileData.username));
        const qs = await getDocs(usernameQ);
        let taken = false;
        qs.forEach(d => {
          if (d.id !== userId) taken = true;
        });
        if (taken) throw new Error('Username is already taken by another user.');
      }

      // رفع الصورة إن وُجدت
      let newPhotoURL = profileData.photoURL || '';
      if (imageFile) {
        const storageRef = ref(storage, `profile_images/${userId}/${imageFile.name}`);
        const snap = await uploadBytes(storageRef, imageFile);
        newPhotoURL = await getDownloadURL(snap.ref);
      }

      const toSave = {
        ...profileData,
        uid: userId,
        photoURL: newPhotoURL,
      };

      await setDoc(doc(db, 'users', userId), toSave, { merge: true });

      setProfileData(toSave);
      setImageFile(null);
      alert('User Profile Saved Successfully!');
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to save.');
    }
    setSaving(false);
  };

  if (loadingData) return <div>Loading user profile...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;

  return (
    <div className="admin-edit-user-page">
      <div className="admin-edit-head">
        <Link to="/admin/users" className="admin-breadcrumb">&larr; Back to All Users</Link>
        <div className="admin-edit-actions">
          <button onClick={handleSave} className="action-button save-button" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <h2 style={{ marginTop: 4 }}>Edit User: {profileData.displayName || '—'}</h2>
      <p style={{ marginTop: 6, color: '#6b7280' }}>Editing profile for: {profileData.email || '—'}</p>

      <main className="user-main-content" style={{ height: 'auto' }}>
        {/* Form */}
        <div className="user-form-container">
          <form className="user-form" onSubmit={(e) => e.preventDefault()}>
            {error && <p className="error-message" style={{ textAlign: 'center' }}>{error}</p>}

            <div className="form-section">
              <h3>Main Details</h3>
              {profileData.photoURL && (
                <img
                  src={profileData.photoURL}
                  alt="Profile"
                  style={{ width: 100, height: 100, borderRadius: '50%', margin: '0 auto' }}
                />
              )}
              <div>
                <label>Profile Image:</label>
                <input type="file" accept="image/*" onChange={handleFileChange} />
              </div>
              <div>
                <label>Display Name:</label>
                <input
                  type="text"
                  name="displayName"
                  value={profileData.displayName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label>Username:</label>
                <input
                  type="text"
                  name="username"
                  value={profileData.username}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Admin Controls</h3>
              <div>
                <label>
                  <input
                    type="checkbox"
                    name="isAdmin"
                    checked={!!profileData.isAdmin}
                    onChange={handleChange}
                    style={{ width: 'auto', marginRight: 10 }}
                  />
                  Is Admin? (Can view and edit all users)
                </label>
              </div>
            </div>

            <div className="form-section">
              <h3>Theme & Type</h3>
              <div>
                <label>Profile Type:</label>
                <select name="profileType" value={profileData.profileType} onChange={handleChange}>
                  <option value="personal">Personal</option>
                  <option value="company">Company</option>
                </select>
              </div>
              <div>
                <label>Profile Theme:</label>
                <select name="theme" value={profileData.theme} onChange={handleChange}>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="forest">Forest</option>
                  <option value="neon">Neon</option>
                  <option value="sunset">Sunset</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <h3>Contact Links</h3>
              <div><label>Phone:</label><input type="tel" name="links.phone" value={profileData.links.phone || ''} onChange={handleChange} /></div>
              <div><label>Email:</label><input type="email" name="links.email" value={profileData.links.email || ''} onChange={handleChange} /></div>
              <div><label>WhatsApp:</label><input type="text" name="links.whatsapp" value={profileData.links.whatsapp || ''} onChange={handleChange} /></div>
              <div><label>Instagram:</label><input type="text" name="links.instagram" value={profileData.links.instagram || ''} onChange={handleChange} /></div>
              <div><label>Facebook:</label><input type="text" name="links.facebook" value={profileData.links.facebook || ''} onChange={handleChange} /></div>
              <div><label>TikTok:</label><input type="text" name="links.tiktok" value={profileData.links.tiktok || ''} onChange={handleChange} /></div>
              <div><label>LinkedIn:</label><input type="text" name="links.linkedin" value={profileData.links.linkedin || ''} onChange={handleChange} /></div>
              <div><label>Snapchat:</label><input type="text" name="links.snapchat" value={profileData.links.snapchat || ''} onChange={handleChange} /></div>
              <div><label>X (Twitter):</label><input type="text" name="links.x" value={profileData.links.x || ''} onChange={handleChange} /></div>
            </div>
          </form>
        </div>

        {/* Preview */}
        <aside className="user-preview-container">
          <div className="preview-frame">
            <LivePreview profile={profileData} />
          </div>
        </aside>
      </main>
    </div>
  );
}

/* ===============================
   مثال مسارات في App.jsx (للتذكير)
   =============================== */
// <Route path="/admin" element={<AdminLayout />}>
//   <Route path="users" element={<AdminUserList />} />
//   <Route path="users/edit/:userId" element={<AdminEditUser />} />
// </Route>
